import { useState, useEffect } from "react";

const useProductVariants = (product) => {
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedVariantOptions, setSelectedVariantOptions] = useState({});
    const [displayImages, setDisplayImages] = useState([]);

    // Helper function to get all variant types from product
    const getVariantTypes = () => {
        if (!product?.variants || product.variants.length === 0) return [];

        const variantTypes = new Set();
        product.variants.forEach(variant => {
            Object.keys(variant).forEach(key => {
                // Exclude non-variant properties
                if (!['id', 'price', 'quantity', 'sku', 'images'].includes(key) &&
                    variant[key] !== null && variant[key] !== undefined) {
                    variantTypes.add(key);
                }
            });
        });

        return Array.from(variantTypes);
    };

    // Helper function to get available options for a specific variant type
    const getVariantOptions = (variantType) => {
        if (!product?.variants) return [];

        const options = new Set();
        product.variants.forEach(variant => {
            if (variant[variantType]) {
                options.add(variant[variantType]);
            }
        });

        return Array.from(options);
    };

    // Helper function to find matching variants based on selected options
    const findMatchingVariant = (selectedOptions) => {
        if (!product?.variants) return null;

        return product.variants.find(variant => {
            return Object.keys(selectedOptions).every(key => {
                return variant[key] === selectedOptions[key];
            });
        });
    };

    // Helper function to get available options for a variant type based on other selected options
    const getAvailableOptions = (variantType) => {
        if (!product?.variants) return [];

        const otherSelectedOptions = { ...selectedVariantOptions };
        delete otherSelectedOptions[variantType];

        const availableVariants = product.variants.filter(variant => {
            return Object.keys(otherSelectedOptions).every(key => {
                return variant[key] === otherSelectedOptions[key];
            });
        });

        const options = new Set();
        availableVariants.forEach(variant => {
            if (variant[variantType]) {
                options.add(variant[variantType]);
            }
        });

        return Array.from(options);
    };

    // Helper function to get images to display based on current selection
    // NEW: Prioritizes option-value images over variant images
    const getDisplayImages = () => {
        // First, try to get images from selected option values (new efficient system)
        if (product?.options && Object.keys(selectedVariantOptions).length > 0) {
            const collectedImages = [];

            // Iterate through options and collect images for selected values
            for (const option of product.options) {
                const optionKey = typeof option.name === 'string'
                    ? option.name.toLowerCase()
                    : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

                const selectedValue = selectedVariantOptions[optionKey];

                if (selectedValue && option.valueImages && option.valueImages[selectedValue]) {
                    const valueImages = option.valueImages[selectedValue];
                    if (valueImages && valueImages.length > 0) {
                        collectedImages.push(...valueImages);
                    }
                }
            }

            // If we found option-value images, use them
            if (collectedImages.length > 0) {
                return collectedImages;
            }
        }

        // Legacy fallback: If a variant is selected and has images, use variant images
        if (selectedVariant && selectedVariant.images && selectedVariant.images.length > 0) {
            return selectedVariant.images;
        }

        // Otherwise, fall back to base product images
        return product?.images || [];
    };

    const handleVariantOptionChange = (variantType, value) => {
        const newSelectedOptions = {
            ...selectedVariantOptions,
            [variantType]: value
        };

        // Check if this combination exists
        const matchingVariant = findMatchingVariant(newSelectedOptions);

        if (matchingVariant) {
            setSelectedVariantOptions(newSelectedOptions);
        } else {
            // If no exact match, update this option and clear conflicting ones
            const updatedOptions = { [variantType]: value };

            // Try to preserve other options if possible
            Object.keys(selectedVariantOptions).forEach(key => {
                if (key !== variantType) {
                    const testOptions = { ...updatedOptions, [key]: selectedVariantOptions[key] };
                    const testVariant = findMatchingVariant(testOptions);
                    if (testVariant) {
                        updatedOptions[key] = selectedVariantOptions[key];
                    }
                }
            });

            setSelectedVariantOptions(updatedOptions);
        }
    };

    // Initialize with first variant if available
    useEffect(() => {
        if (product?.variants && product.variants.length > 0) {
            const firstVariant = product.variants[0];
            setSelectedVariant(firstVariant);

            // Initialize selected variant options
            const initialOptions = {};
            Object.keys(firstVariant).forEach(key => {
                if (!['id', 'price', 'quantity', 'sku', 'images'].includes(key) &&
                    firstVariant[key] !== null && firstVariant[key] !== undefined) {
                    initialOptions[key] = firstVariant[key];
                }
            });
            setSelectedVariantOptions(initialOptions);
        }
    }, [product]);

    // Update selected variant when options change
    useEffect(() => {
        if (Object.keys(selectedVariantOptions).length > 0) {
            const matchingVariant = findMatchingVariant(selectedVariantOptions);
            setSelectedVariant(matchingVariant);
        }
    }, [selectedVariantOptions, product]);

    // Update display images when variant changes
    useEffect(() => {
        const newDisplayImages = getDisplayImages();
        setDisplayImages(newDisplayImages);
    }, [selectedVariant, product]);

    return {
        selectedVariant,
        selectedVariantOptions,
        displayImages,
        getVariantTypes,
        getVariantOptions,
        getAvailableOptions,
        handleVariantOptionChange,
        setSelectedVariantOptions,
        getDisplayImages
    };
};

export default useProductVariants;